import { useEffect, useRef, useState } from "react";
import { HumanSupportBanner } from "@/components/recruiter/HumanSupport";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Caption, NativeSelect, PageHeader, Spinner, TierBadge } from "@/components/Common";
import { Sections } from "@/components/Rich";
import { JobPicker } from "@/components/JobPicker";
import { ResultScore, extractScore } from "@/components/ResultScore";
import { ScoreHistory } from "@/components/ScoreHistory";
import { getHistory, getPassMin, recordScore } from "@/lib/atsScores";
import { InlineResumeUpload } from "@/components/InlineResumeUpload";
import { celebrate } from "@/components/ScoreToast";
import { findTailor, loadTools } from "@/lib/tools";
import { ShareBar } from "@/components/ShareBar";
import { TailorCard } from "@/components/TailorCard";
import { ScoreBurst } from "@/components/ScoreBurst";
import { boostContentOf, ideasOf, optimizedOf, stashTailor, tailorUrl } from "@/lib/tailor";
import { BestResume } from "@/components/BestResume";
const { ArrowRight, FilePen, FileUp, Gauge, Rocket } = Icons;
import { api, errMsg, humanize, tierRank } from "@/lib/api";
import { cn } from "@/lib/utils";

const toPascal = (s) => String(s || "").replace(/(^|[-_\s])(\w)/g, (_, __, c) => c.toUpperCase()).replace(/-outline$/i, "");
const ToolIcon = ({ name, className }) => {
  const I = Icons[toPascal(name)] || Icons.Sparkles;
  return <I className={className} />;
};

const normInputs = (inputs) => {
  if (!inputs) return [];
  if (Array.isArray(inputs)) return inputs.map((i) => (typeof i === "string" ? { key: i, label: humanize(i) } : { ...i, key: i.key || i.id || i.name }));
  return Object.entries(inputs).map(([k, v]) => (typeof v === "string" ? { key: k, label: v } : { key: k, ...v }));
};

const jobFlag = (t) => [t.needs_job, t.requires_job, t.job_required, t.needsJob].some((v) => v === true || v === "true" || v === 1) || /apply/i.test(t.category || "");

export const ToolRunner = ({ tool: initialTool, onClose, initialJob }) => {
  const [tool, setTool] = useState(initialTool);
  const [returnTo, setReturnTo] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const fields = normInputs(tool.inputs);
  const [vals, setVals] = useState({});
  const [jobId, setJobId] = useState(initialJob?.id || "");
  const [needsJob, setNeedsJob] = useState(() => jobFlag(tool));
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const contentRef = useRef(null);
  const ranRef = useRef(false);
  const atsLike = (t) => /ats/i.test(`${t.id} ${t.title}`);
  const isAts = atsLike(tool);
  const score = extractScore(res, isAts);
  const tailor = findTailor(catalog?.tools);
  useEffect(() => { loadTools().then(setCatalog); }, []);
  useEffect(() => { if (res && contentRef.current) contentRef.current.scrollTo({ top: 0, behavior: "smooth" }); }, [res]);
  useEffect(() => {
    api.get(`/ai/tools/${tool.id}/last`).then((r) => { if (!ranRef.current && r.data && (r.data.sections || r.data.result)) setRes(r.data.result || r.data); }).catch(() => {});
  }, [tool.id]);
  const run = async (t = tool) => {
    if ((needsJob || jobFlag(t)) && !jobId) return toast.error("Choose a job above, or search for one, then hit Run");
    ranRef.current = true;
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/tools/${t.id}`, { job_id: jobId || null, inputs: t === tool ? vals : {} });
      setRes(data);
      if (atsLike(t) && jobId) {
        const prev = getHistory(jobId);
        const s = extractScore(data, true);
        recordScore(jobId, s, t.id);
        const last = prev.at(-1)?.score, min = getPassMin();
        if (s != null && prev.length && !(last < min && s >= min)) celebrate(Math.round(s) - last, Math.round(s));
      }
    } catch (e) {
      const m = errMsg(e);
      if (!jobId && /job/i.test(m)) { setNeedsJob(true); toast.error("This tool needs a job. Pick one above, then hit Run."); }
      else toast.error(m);
    } finally { setBusy(false); }
  };
  const switchTo = (t) => { setTool(t); setRes(null); setVals({}); setNeedsJob(jobFlag(t) || !!jobId); };
  const boost = () => {
    if (tierRank(tailor.tier) > tierRank(catalog?.tier)) { onClose(); window.dispatchEvent(new CustomEvent("rpopro:upgrade", { detail: { tier: tailor.tier, message: `${tailor.title} needs ${tailor.tier}` } })); return; }
    setReturnTo(tool); switchTo(tailor); run(tailor);
  };
  const recheck = () => { const t = returnTo; setReturnTo(null); setUploaded(false); switchTo(t); run(t); };
  const [uploaded, setUploaded] = useState(false);
  const navigate = useNavigate();
  const jobInfo = initialJob?.id === jobId ? initialJob : null;
  const openStudio = () => { stashTailor({ jobId, job: jobInfo, toolId: tool.id, toolTitle: tool.title, ideas: ideasOf(res), content: boostContentOf(res) }); onClose(); navigate(tailorUrl(jobId)); };
  const StudioButton = ({ testid }) => (
    <Button onClick={openStudio} className="group h-auto min-h-10 whitespace-normal rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F3C649] px-5 font-semibold text-black shadow-[0_8px_30px_-8px_rgba(212,175,55,0.6)] transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-[0_12px_36px_-8px_rgba(212,175,55,0.8)]" data-testid={testid}>
      <FilePen className="h-4 w-4" />Edit my resume in Resume Studio<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
    </Button>
  );
  const goResume = () => { onClose(); navigate(`/profile?focus=resume${jobId ? `&job=${encodeURIComponent(jobId)}` : ""}`); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent ref={contentRef} onInteractOutside={(e) => { if (e.target?.closest?.("[data-testid=auto-recheck-panel]")) e.preventDefault(); }} className="max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] overflow-y-auto overflow-x-hidden border-[#27272A] bg-[#18181B] sm:max-w-2xl [&>*]:min-w-0" data-testid="tool-runner-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><ToolIcon name={tool.icon} className="h-5 w-5 text-[#D4AF37]" />{tool.title}</DialogTitle></DialogHeader>
        {returnTo && (
          <div className="flex flex-col gap-2 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-3" data-testid="recheck-banner">
            <span className="text-sm text-zinc-200">{uploaded ? "Optimized resume uploaded. Re-check your pass rate now." : "Apply these edits to your resume, upload the optimized version right here, then re-check your pass rate."}</span>
            <div className="flex flex-wrap gap-2">
              {!uploaded && <InlineResumeUpload size="sm" skipJobId={jobId} onDone={() => setUploaded(true)} />}
              <Button size="sm" onClick={recheck} disabled={busy} className={uploaded ? "animate-pulse" : ""} data-testid="recheck-ats-button"><Gauge className="h-4 w-4" />Re-check ATS pass rate</Button>
            </div>
            <div className="flex flex-wrap items-center gap-3"><StudioButton testid="edit-in-resume-studio" /><button onClick={goResume} className="text-[11px] text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline" data-testid="replace-resume-button">Manage resumes on Profile</button></div>
          </div>
        )}
        {score != null && (isAts && jobId ? <ScoreBurst jobId={jobId} className="block"><ResultScore score={score} title="ATS pass rate" /></ScoreBurst> : <ResultScore score={score} title={isAts ? "ATS pass rate" : "Score"} />)}
        {isAts && res && !returnTo && <div className="flex flex-wrap items-center gap-2" data-testid="ats-studio-cta"><StudioButton testid="ats-edit-in-resume-studio" /><span className="text-xs text-zinc-500">Apply these ideas, then re-check your score.</span></div>}
        {isAts && score != null && score < 75 && tailor && jobId && !uploaded && (
          <Button onClick={boost} disabled={busy} variant="outline" className="h-auto min-h-11 w-full whitespace-normal rounded-full border-emerald-500/50 bg-emerald-500/5 py-2 text-center text-emerald-300 transition-colors hover:border-emerald-400 hover:bg-emerald-500/15 hover:text-emerald-200" data-testid="boost-score-button">
            <Rocket className="h-4 w-4" />Boost my score: tailor my resume for this job
          </Button>
        )}
        {isAts && score != null && score < 75 && !returnTo && (
          uploaded ? (
            <Button onClick={() => { setUploaded(false); run(); }} disabled={busy} className="h-auto min-h-10 w-full whitespace-normal rounded-full py-2" data-testid="recheck-after-upload-button"><Gauge className="h-4 w-4" />Resume updated. Re-check pass rate</Button>
          ) : (
            <div className="flex flex-col items-center gap-1.5">
              <InlineResumeUpload skipJobId={jobId} className="w-full rounded-full" label="Already optimized? Upload it as your main resume" onDone={() => setUploaded(true)} />
              <button onClick={goResume} className="text-[11px] text-zinc-500 hover:text-zinc-300 hover:underline" data-testid="go-main-resume-button">or manage resumes on Profile</button>
            </div>
          )
        )}
        {isAts && jobId && <ScoreHistory jobId={jobId} />}
        {isAts && jobId && <BestResume jobId={jobId} />}
        <p className="text-sm text-zinc-400">{tool.subtitle}</p>
        <div className="space-y-4">
          <div className="space-y-1.5" data-testid="tool-job-field">
            <Label className="text-xs text-zinc-400">Job {needsJob ? <span className="text-[#D4AF37]">· required</span> : <span className="text-zinc-600">· optional</span>}</Label>
            <JobPicker value={jobId} onChange={setJobId} optional={!needsJob} autoSelect={needsJob} initialJob={initialJob} />
          </div>
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label className="text-xs text-zinc-400">{f.label || humanize(f.key)}</Label>
              {f.options ? (
                <NativeSelect value={vals[f.key] || ""} onChange={(v) => setVals({ ...vals, [f.key]: v })} className="w-full" data-testid={`tool-input-${f.key}`}
                  options={[{ value: "", label: "Select..." }, ...f.options.map((o) => (typeof o === "string" ? { value: o, label: o } : { value: o.value, label: o.label || o.value }))]} />
              ) : f.type === "textarea" || f.multiline ? (
                <Textarea rows={4} placeholder={f.placeholder} value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className="bg-[#09090B]" data-testid={`tool-input-${f.key}`} />
              ) : (
                <Input placeholder={f.placeholder} value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className="bg-[#09090B]" data-testid={`tool-input-${f.key}`} />
              )}
            </div>
          ))}
          <Button onClick={() => run()} disabled={busy} className="w-full" data-testid="run-tool-button">{busy ? <><Spinner />Thinking...</> : "Run"}</Button>
        </div>
        {res && <div className="mt-4" data-testid="tool-result"><Sections result={res} /></div>}
        {!isAts && (!returnTo || optimizedOf(res)) && <TailorCard res={res} tool={tool} jobId={jobId} job={jobInfo} onClose={onClose} />}
        {res && (isAts || /kit|tailored|one.?click/i.test(`${tool.id} ${tool.title}`)) && <ShareBar tool={tool} jobId={jobId} res={res} score={score} />}
      </DialogContent>
    </Dialog>
  );
};

const Tile = ({ item, locked, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={cn("group fade-up relative flex flex-col items-start rounded-xl border border-[#27272A] bg-[#18181B] p-5 text-left transition-[border-color,transform] hover:-translate-y-0.5 hover:border-[#D4AF37]/40", locked && "opacity-60")}>
    <div className="mb-4 flex w-full items-center justify-between">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#D4AF37]/10"><ToolIcon name={item.icon} className="h-4 w-4 text-[#D4AF37]" /></span>
      <div className="flex items-center gap-1.5">{item.tier && item.tier !== "free" && <TierBadge tier={item.tier} />}{locked && <Icons.Lock className="h-3.5 w-3.5 text-zinc-400" data-testid="lock-icon" />}</div>
    </div>
    <h3 className="font-display text-base font-semibold text-zinc-100 group-hover:text-[#F3C649]">{item.title}</h3>
    {item.subtitle && <p className="mt-1 text-xs text-zinc-500">{item.subtitle}</p>}
  </button>
);

export default function AIStudio() {
  const [data, setData] = useState(null);
  const [tool, setTool] = useState(null);
  const [linkJob, setLinkJob] = useState(null);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  useEffect(() => { api.get("/ai/tools").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => {
    const id = params.get("tool");
    if (!id || !data) return;
    const t = data.tools?.find((x) => x.id === id);
    if (!t) { setParams({}, { replace: true }); toast.message("That tool isn't available anymore."); return; }
    const job = params.get("job");
    setParams({}, { replace: true });
    if (tierRank(t.tier) > tierRank(data.tier)) { navigate("/upgrade", { state: { reason: { tier: t.tier, message: `${t.title} needs ${t.tier}` } } }); return; }
    setLinkJob(job ? { id: job, title: "Linked job", company: "" } : null);
    setTool(t);
    if (job) api.get(`/jobs/${encodeURIComponent(job)}`).then((r) => r.data?.job && setLinkJob({ id: job, title: r.data.job.title, company: r.data.job.company })).catch(() => {});
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <Spinner className="h-6 w-6 text-[#D4AF37]" />;
  const userRank = tierRank(data.tier);
  const locked = (t) => tierRank(t.tier) > userRank;
  const groups = (data.tools || []).reduce((g, t) => ({ ...g, [t.category || "Tools"]: [...(g[t.category || "Tools"] || []), t] }), {});
  const gate = (t, fn) => (locked(t) ? navigate("/upgrade", { state: { reason: { tier: t.tier, message: `${t.title} needs ${t.tier}` } } }) : fn());

  return (
    <div>
      <PageHeader title="AI Studio" subtitle="Career tools, simulations and coaching, tuned to your resume.">
        <TierBadge tier={data.tier} className="text-xs" />
      </PageHeader>
      <HumanSupportBanner where="interview" />
      <Link to="/wellbeing" data-testid="studio-wellbeing-card"
        className="mb-10 flex items-center gap-4 rounded-[20px] border border-[#D4AF37]/40 bg-[#33290D]/50 p-5 transition-colors hover:bg-[#33290D]">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#D4AF37] text-[#09090B]"><Icons.Heart className="h-5 w-5" fill="currentColor" /></span>
        <span className="min-w-0 flex-1"><span className="block font-display text-lg text-zinc-100">Well-Being</span><span className="block text-sm text-zinc-400">Check-ins, wisdom, journeys, your journal and your career companion.</span></span>
        <Icons.ChevronRight className="h-5 w-5 text-[#D4AF37]" />
      </Link>
      {data.chats?.length > 0 && (
        <section className="mb-12">
          <Caption className="mb-4">Live conversations</Caption>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {data.chats.map((c) => (
              <Tile key={c.id} item={{ ...c, icon: c.icon || (c.id === "wellness" ? "heart-pulse" : "message-square") }} locked={locked(c)} testid={`chat-tile-${c.id}`}
                onClick={() => gate(c, () => navigate(`/ai-studio/chat/${c.id}`, { state: { chat: c } }))} />
            ))}
          </div>
        </section>
      )}
      {Object.entries(groups).map(([cat, tools]) => (
        <section key={cat} className="mb-12">
          <Caption className="mb-4">{cat}</Caption>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {tools.map((t) => <Tile key={t.id} item={t} locked={locked(t)} testid={`tool-tile-${t.id}`} onClick={() => gate(t, () => setTool(t))} />)}
          </div>
        </section>
      ))}
      {tool && <ToolRunner key={`${tool.id}-${linkJob?.id || ""}`} tool={tool} initialJob={linkJob} onClose={() => { setTool(null); setLinkJob(null); }} />}
    </div>
  );
}
